/**
 * Main CMDB Application Component
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// Aspecto (color + forma): única fuente de verdad en `theme/appearance.js`
import { bootAppearance, resolveAppearance } from './theme/appearance';
import { ViewsProvider } from './views/ViewsContext';
import api from './services/api';
import idpService from './services/idp';
import { changeLanguage, currentLanguage } from './i18n/config';

// Components
import MainLayout from './components/MainLayout';
import InstallAppPrompt from './components/InstallAppPrompt';
import Dashboard from './components/Dashboard';
import UnderConstruction from './components/UnderConstruction';
import LocationsList from './components/LocationsList';
import RacksList from './components/RacksList';
import RackViewer from './components/RackViewer';
import ServersList from './components/ServersList';
import ServersDashboard from './components/ServersDashboard';
import PortElementsList from './components/PortElementsList';
import AccessPointsList from './components/AccessPointsList';
import CablingDesigner from './components/CablingDesigner';
import CablingCanvas from './components/CablingCanvas';
import FloorPlanMap from './components/FloorPlanMap';
import WallOutletsList from './components/WallOutletsList';
import IPPhonesList from './components/IPPhonesList';
import PrintersList from './components/PrintersList';
import WorkstationsList from './components/WorkstationsList';
import WorkstationsDashboard from './components/WorkstationsDashboard';
import WorkstationsUpdatesPage from './components/WorkstationsUpdatesPage';
import WorkstationsSoftwarePage from './components/WorkstationsSoftwarePage';
import WorkstationsVulnerabilitiesPage from './components/WorkstationsVulnerabilitiesPage';
import WorkPlansList from './components/WorkPlansList';
import WorkPlanDetail from './components/WorkPlanDetail';
import PeripheralsList from './components/PeripheralsList';
import StorageArraysList from './components/StorageArraysList';
import ChassisList from './components/ChassisList';
import IPAMDashboard from './components/IPAMDashboard';
import IPAMBlocks from './components/IPAMBlocks';
import IPAMSubnets from './components/IPAMSubnets';
import IPAMExplorer from './components/IPAMExplorer';
import IPAMAssignments from './components/IPAMAssignments';
import IPAMVlans from './components/IPAMVlans';
import IPAMIPs from './components/IPAMIPs';
import IPAMVirtualGroups from './components/IPAMVirtualGroups';
import IPAMDhcp from './components/IPAMDhcp';
import VirtHypervisorsList from './components/VirtHypervisorsList';
import VirtClustersList from './components/VirtClustersList';
import VirtVSwitchesList from './components/VirtVSwitchesList';
import VirtVMsList from './components/VirtVMsList';
import VMsDashboard from './components/VMsDashboard';
import WifiControllersList from './components/WifiControllersList';
import WifiAccessPointsList from './components/WifiAccessPointsList';
import WifiDashboard from './components/WifiDashboard';
import VirtDashboard from './components/VirtDashboard';
import VirtTopologyCanvas from './components/VirtTopologyCanvas';
import WifiTopologyCanvas from './components/WifiTopologyCanvas';
import NetworkTopologyCanvas from './components/NetworkTopologyCanvas';
import NetworkDiscoveredList from './components/NetworkDiscoveredList';
import RadiusServersList from './components/RadiusServersList';
import FirewallsList from './components/FirewallsList';
import RoutersList from './components/RoutersList';
import UpsList from './components/UpsList';
import ISPOperators from './components/ISPOperators';
import ISPCircuits from './components/ISPCircuits';
import AuditLog from './components/AuditLog';
import Settings from './components/Settings';
import NamingManager from './components/NamingManager';
import DeviceCatalogManager from './components/DeviceCatalogManager';
import RolesManager from './components/RolesManager';
import SetupManager from './components/SetupManager';
import AgentSettings from './components/AgentSettings';
import AgentMappingProfile from './components/AgentMappingProfile';
import PlatformAdmin from './components/PlatformAdmin';
import Login from './pages/Login';
import AgentDownload from './pages/AgentDownload';
// Una excepción en el render de una página pública dejaba `#root` vacío y, con
// el tema oscuro, eso se ve como una pantalla en negro sin ningún mensaje.
import PublicErrorBoundary from './components/PublicErrorBoundary';
import MiPerfil from './pages/MiPerfil';
import { SocketProvider } from './contexts/SocketContext';
import DocumentWorkspace from './pages/DocumentWorkspace';
import PowerCanvas from './components/PowerCanvas';
import NetworkCanvas from './components/NetworkCanvas';
import SwitchWiringCanvas from './components/SwitchWiringCanvas';
import ConnectionsMatrix from './components/ConnectionsMatrix';
import DatacenterDiagram from './components/DatacenterDiagram';
import ServicesList from './components/ServicesList';
import ServiceDependencyMap from './components/ServiceDependencyMap';
import ServiceDesignerList from './components/ServiceDesignerList';
import ServiceDesignerGraph from './components/ServiceDesignerGraph';
import ServiceFarmsList from './components/ServiceFarmsList';
import BackupPoliciesList from './components/BackupPoliciesList';
import DatabaseInstancesList from './components/DatabaseInstancesList';
import WebSitesList from './components/WebSitesList';
import LicensesList from './components/LicensesList';
import DocsLibrary from './components/DocsLibrary';
import DocsEditor from './components/DocsEditor';
import CompanyDocs from './pages/CompanyDocs';
import FeedbackHub from './components/FeedbackHub';
import ContactsList from './components/ContactsList';
import DnsManager from './components/DnsManager';
import CertificatesList from './components/CertificatesList';
import DnsProviderSettings from './components/DnsProviderSettings';
import CamerasList from './components/CamerasList';
import RecordersList from './components/RecordersList';
import CamerasTopologyCanvas from './components/CamerasTopologyCanvas';
import VulnerabilitiesPanel from './components/VulnerabilitiesPanel';
import ManualCMDB from './components/ManualCMDB';
import AcademyPage from './academy/AcademyPage';
import OnboardingWizard from './components/OnboardingWizard';

// Styles
import './styles/app.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Empty string → relative URLs (/api/…) proxied by nginx.
// For local dev without Docker, set REACT_APP_API_URL in .env.development.
const API_BASE = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '').replace(/\/$/, '');

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [enrichedCompanies, setEnrichedCompanies] = useState([]);
  // Aspecto del servidor: lo que el usuario guardó y lo que fija su empresa.
  const [companyTheme, setCompanyTheme] = useState(null);
  // Idioma: se resuelve antes de pintar la app y decide si hay que enseñar la
  // pantalla de bienvenida (primera vez del usuario en esta CMDB).
  const [languagePrefReady, setLanguagePrefReady] = useState(false);
  const [onboardingNeeded, setOnboardingNeeded] = useState(false);
  const [onboardingSaving, setOnboardingSaving] = useState(false);

  const resolveInitialCompany = useCallback((userLike) => {
    const authorized = Array.isArray(userLike?.authorized_companies)
      ? userLike.authorized_companies
      : [];
    const authorizedIds = authorized
      .map((entry) => {
        const value = entry != null && typeof entry === 'object'
          ? (entry.id ?? entry.company_id ?? entry.slug)
          : entry;
        return value != null ? String(value) : null;
      })
      .filter(Boolean);

    if (authorizedIds.length === 0) {
      return null;
    }

    // La empresa elegida en este navegador MANDA sobre la principal del usuario,
    // para no volver a la de por defecto al recargar (F5).
    // OJO: el token no siempre trae la lista completa de empresas (a veces solo
    // el `company_id` principal), así que NO se filtra aquí por esa lista: si
    // se hiciera, al recargar volvería siempre a la principal. La validación
    // real la hace el efecto de `enrichedCompanies`, que sí conoce la lista
    // completa y corrige la elección si el usuario ya no tiene acceso.
    const saved = localStorage.getItem('selectedCompany');
    if (saved) {
      return saved;
    }

    const primaryCompany = userLike?.company_id != null ? String(userLike.company_id) : null;
    if (primaryCompany && authorizedIds.includes(primaryCompany)) {
      return primaryCompany;
    }

    return authorizedIds[0];
  }, []);

  // El aspecto (skin + paleta) ya se aplica en `index.js` antes de pintar,
  // para que no haya parpadeo. Aquí solo se re-aplica por si el componente
  // se vuelve a montar (recarga en caliente, cambio de sesión…).
  useEffect(() => {
    bootAppearance();
  }, []);

  // ── Idioma del usuario ────────────────────────────────────────────────────
  // Se pide al servidor después de autenticar. Si el usuario ya eligió, se
  // aplica antes de pintar (sin parpadeo de español); si no, se deja el que
  // detectó el navegador y se abre el onboarding UNA vez.
  useEffect(() => {
    let cancelled = false;
    if (!authenticated) {
      setLanguagePrefReady(false);
      return undefined;
    }

    setLanguagePrefReady(false);
    let timeoutId = null;
    const timeout = new Promise((_resolve, reject) => {
      timeoutId = setTimeout(() => reject(new Error('language-pref-timeout')), 8000);
    });
    Promise.race([api.getLanguagePreference(), timeout])
      .then(async (data) => {
        const language = data?.language || currentLanguage();
        await changeLanguage(language, { persist: Boolean(data?.language) });
        if (cancelled) return;
        setOnboardingNeeded(!data?.onboarding_done);
      })
      .catch(async () => {
        // Backend antiguo, sin migrar o lento: se queda el idioma del navegador
        // y no se molesta con un onboarding que no se podría guardar.
        await changeLanguage(currentLanguage(), { persist: false });
        if (!cancelled) setOnboardingNeeded(false);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (!cancelled) setLanguagePrefReady(true);
      });

    return () => { cancelled = true; };
  }, [authenticated]);

  const handleOnboardingComplete = useCallback(
    async ({ language, skin = null, palette = null, themeChosen = false }) => {
      setOnboardingSaving(true);
      try {
        await changeLanguage(language, { persist: true });
      } catch {
        // Aunque falle la carga remota, el idioma local ya está aplicado.
      }

      // El asistente se cierra en cuanto el idioma está aplicado: lo de guardar
      // el aspecto y la marca de onboarding se hace en segundo plano. Si algo
      // falla, la próxima vez se vuelve a preguntar; no se bloquea la entrada.
      if (themeChosen && selectedCompany && selectedCompany !== 'all') {
        api.saveThemePreferences(selectedCompany, { skin, palette })
          .then((data) => {
            if (!data) return;
            setCompanyTheme(data);
            resolveAppearance(data);
          })
          .catch(() => {});
      }

      api.saveLanguagePreference(language, { onboardingDone: true })
        .catch(() => {})
        .finally(() => setOnboardingSaving(false));

      setOnboardingNeeded(false);
    },
    [selectedCompany]
  );

  // Aspecto definitivo para la empresa activa: preferencia guardada por el
  // usuario (en cualquier dispositivo) y aspecto por defecto de la empresa.
  // Lo elegido en este navegador sigue teniendo prioridad — ver `resolveAppearance`.
  useEffect(() => {
    let cancelled = false;
    if (!selectedCompany || selectedCompany === 'all') return undefined;

    api.getThemePreferences(selectedCompany)
      .then((data) => {
        if (cancelled || !data) return;
        setCompanyTheme(data);
        resolveAppearance(data);
      })
      .catch(() => {
        // Si la preferencia no se puede leer, se queda el aspecto local.
      });

    return () => { cancelled = true; };
  }, [selectedCompany]);

  useEffect(() => {
    // Check authentication on mount
    const checkAuth = async () => {
      try {
        // ── Vuelta del SSO ────────────────────────────────────────────────
        // El IdP devuelve un CÓDIGO de un solo uso (`?code=`), no el token: el
        // token en la URL acababa en el historial del navegador, en el
        // `Referer` y en los logs. El backend lo canjea contra el IdP y aquí
        // solo se guarda el resultado.
        const urlParams = new URLSearchParams(window.location.search);
        const ssoCode = urlParams.get('code');
        let idpToken = urlParams.get('idp_token') || urlParams.get('token');

        if (ssoCode) {
          try {
            idpToken = await idpService.exchangeCode(ssoCode);
            // El código se consume en el canje: que no quede en la barra de
            // direcciones ni se pueda reintentar desde el historial.
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch (err) {
            console.error('[SSO] No se pudo canjear el código:', err);
            idpToken = null;
          }
        } else if (idpToken) {
          console.warn('[SSO] Token recibido en la URL: camino heredado, en desuso');
        }

        if (idpToken) {
          console.log('[SSO] Token recibido de IdP, decodificando y guardando');
          
          // Decode first to verify the token is not already expired
          try {
            const decoded = jwtDecode(idpToken);
            console.log('[SSO] Decoded token:', decoded);

            // Guard: reject if the token is already expired (clock skew, etc.)
            if (decoded.exp && decoded.exp * 1000 < Date.now()) {
              console.warn('[SSO] Token received but already expired — requesting a new one');
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              const returnUrl = encodeURIComponent(window.location.origin + '/');
              window.location.href = `${API_BASE}/api/auth/login?return_to=${returnUrl}`;
              return;
            }

            // Store the token from IdP
            localStorage.setItem('token', idpToken);
            
            // Create user object from decoded token
            const userInfo = {
              id: decoded.user_id,
              email: decoded.email,
              company_id: decoded.company_id,
              company_slug: decoded.company_slug,
              app_code: decoded.app_code,
              app_role: decoded.app_role,
              is_superadmin: decoded.is_superadmin,
              authorized_companies: decoded.authorized_companies || []
            };
            
            localStorage.setItem('user', JSON.stringify(userInfo));
            setAuthenticated(true);
            setUserInfo(userInfo);
            setSelectedCompany(resolveInitialCompany(userInfo));
            console.log('[SSO] User authenticated via SSO:', userInfo);
          } catch (err) {
            console.error('[SSO] Failed to decode token:', err);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setAuthenticated(false);
          }
          
          // Clean up the URL (remove token parameter)
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Check if user is already authenticated via local token
          const token = localStorage.getItem('token');
          const userStr = localStorage.getItem('user');
          
          if (token) {
            try {
              const decoded = jwtDecode(token);
              
              if (decoded.exp && decoded.exp * 1000 < Date.now()) {
                console.log('[AUTH] Token expired');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setAuthenticated(false);
              } else {
                const user = userStr ? JSON.parse(userStr) : {
                  id: decoded.user_id,
                  email: decoded.email,
                  company_id: decoded.company_id,
                  authorized_companies: decoded.authorized_companies || [],
                  app_role: decoded.app_role || decoded.role,
                  is_superadmin: decoded.is_superadmin
                };
                localStorage.setItem('user', JSON.stringify(user));
                setAuthenticated(true);
                setUserInfo(user);
                setSelectedCompany(resolveInitialCompany(user));
              }
            } catch (err) {
              console.error('[AUTH] Token decode failed:', err);
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setAuthenticated(false);
            }
          }
        }
      } catch (err) {
        console.error('Auth check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [resolveInitialCompany]);

  // ── Enrich company list with names from API ──
  useEffect(() => {
    if (!authenticated) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/companies/authorized`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'include'
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const companies = data.companies || [];
        if (companies.length > 0) setEnrichedCompanies(companies);
      })
      .catch(() => {});
  }, [authenticated]);

  // La empresa guardada tiene que existir en la lista real. Si el usuario ya no
  // tiene acceso (empresa borrada, permiso retirado…), se vuelve a la principal
  // en vez de quedarse con una pantalla en blanco o en 403.
  useEffect(() => {
    if (!selectedCompany || selectedCompany === 'all') return;
    if (enrichedCompanies.length === 0) return;
    const ids = enrichedCompanies.map((c) => String(c.id));
    if (ids.includes(String(selectedCompany))) return;
    const fallback = userInfo?.company_id != null ? String(userInfo.company_id) : null;
    const next = fallback && ids.includes(fallback) ? fallback : ids[0];
    if (next) {
      setSelectedCompany(next);
      localStorage.setItem('selectedCompany', next);
    }
  }, [enrichedCompanies, selectedCompany, userInfo]);

  // ── Session-expired event listener ──
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener('cmdb:session-expired', onExpired);
    return () => window.removeEventListener('cmdb:session-expired', onExpired);
  }, []);

  const handleReconnect = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Go straight to IdP SSO — the IdP session is likely still active
    // so the user will get a fresh token without typing credentials.
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${API_BASE}/api/auth/login?return_to=${returnUrl}`;
  };

  const handleCompanyChange = (newCompanyId) => {
    const normalized = newCompanyId != null ? String(newCompanyId) : null;
    console.log('[COMPANY] Switching to company:', normalized);
    setSelectedCompany(normalized);
    // Persist so it survives page refresh
    if (normalized) {
      localStorage.setItem('selectedCompany', normalized);
    } else {
      localStorage.removeItem('selectedCompany');
    }
  };

  const handleLogout = async () => {
    try {
      // Call CMDB backend logout endpoint
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }).catch(() => {
        // Continue even if backend logout fails
      });
      
      // Use IdP service to perform logout
      await idpService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  return (
    <SocketProvider>
    <Router>
      {/**
       * Rutas de primer nivel. La página pública del agente —la que se pasa a
       * quien va a instalar en un servidor— va FUERA del portón de sesión:
       * ese técnico no tiene cuenta en el CMDB y no la necesita para bajar el
       * instalador. El resto de la app sigue igual, en el `*`.
       */}
      <Routes>
        <Route
          path="/instalar-agente"
          element={(
            <PublicErrorBoundary reloadHref="/instalar-agente">
              <AgentDownload />
            </PublicErrorBoundary>
          )}
        />

        <Route
          path="*"
          element={(loading || (authenticated && !languagePrefReady)) ? (
            <div className="container-fluid d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !authenticated ? (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          ) : (
        <>
        <MainLayout
          userInfo={userInfo}
          selectedCompany={selectedCompany}
          authorizedCompanies={enrichedCompanies.length > 0 ? enrichedCompanies : (userInfo?.authorized_companies || [])}
          onCompanyChange={handleCompanyChange}
          onLogout={handleLogout}
          sessionExpired={sessionExpired}
          onReconnect={handleReconnect}
        >
          {/* En qué vista se mira cada listado. Vive aquí (una sola vez) para
              que el conmutador y la preferencia sean los mismos en todas las
              pantallas: cada lista solo declara sus vistas y su predeterminada. */}
          <ViewsProvider companyId={selectedCompany}>
          <Routes>
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard companyId={selectedCompany} />} />
            
            {/* Ubicaciones */}
            <Route path="/locations" element={<LocationsList companyId={selectedCompany} />} />
              <Route path="/contacts" element={<ContactsList companyId={selectedCompany} />} />
              <Route path="/dns" element={<DnsManager companyId={selectedCompany} />} />
              <Route path="/certificates" element={<CertificatesList companyId={selectedCompany} />} />
              <Route path="/admin/dns" element={<DnsProviderSettings />} />
              <Route path="/cameras/list" element={<CamerasList companyId={selectedCompany} />} />
              <Route path="/cameras/recorders" element={<RecordersList companyId={selectedCompany} />} />
              <Route path="/cameras/topology" element={<CamerasTopologyCanvas companyId={selectedCompany} />} />
              <Route path="/cameras/map" element={<FloorPlanMap companyId={selectedCompany} mode="cameras" />} />
              <Route
                path="/security/vulnerabilities"
                element={(
                  <div className="list-page">
                    <VulnerabilitiesPanel companyId={selectedCompany} />
                  </div>
                )}
              />
            {/* Infraestructura Física */}
            <Route path="/infrastructure/racks" element={<RacksList companyId={selectedCompany} />} />
            <Route path="/infrastructure/racks/:locationId" element={<RacksList companyId={selectedCompany} />} />
            <Route path="/infrastructure/rack/:rackId" element={<RackViewer companyId={selectedCompany} />} />
            <Route path="/infrastructure/servers" element={<ServersDashboard companyId={selectedCompany} />} />
            <Route path="/infrastructure/servers/list" element={<ServersList companyId={selectedCompany} />} />
            <Route path="/infrastructure/storage-arrays" element={<StorageArraysList companyId={selectedCompany} />} />
            <Route path="/infrastructure/switches" element={<PortElementsList key="switch" elementType="switch" companyId={selectedCompany} />} />
            <Route path="/infrastructure/routers" element={<UnderConstruction sectionName="Routers" sectionIcon="🌐" />} />
            <Route path="/infrastructure/firewalls" element={<FirewallsList companyId={selectedCompany} />} />
            <Route path="/infrastructure/pdus" element={<PortElementsList key="pdu" elementType="pdu" companyId={selectedCompany} />} />
            <Route path="/infrastructure/ups" element={<UpsList companyId={selectedCompany} />} />
            <Route path="/infrastructure/patch-panels" element={<PortElementsList key="panel" elementType="panel" companyId={selectedCompany} />} />
          <Route path="/infrastructure/accesses" element={<AccessPointsList companyId={selectedCompany} />} />

            {/* Cableado: las cinco pestañas que vivían dentro de una sola página
                son ahora entradas del submenú lateral «Cableado». Se paga menos
                alto de cabecera (clave en portátiles de 14") y cada vista tiene
                su propia miga. Las rutas antiguas redirigen para no romper
                marcadores, manuales ni enlaces guardados. */}
            <Route path="/cabling" element={<Navigate to="/cabling/designer" replace />} />
            <Route path="/cabling/designer" element={<CablingDesigner companyId={selectedCompany} />} />
            <Route path="/cabling/wiring" element={<SwitchWiringCanvas companyId={selectedCompany} />} />
            <Route path="/cabling/map" element={<FloorPlanMap companyId={selectedCompany} />} />
            <Route path="/cabling/outlets" element={<WallOutletsList companyId={selectedCompany} />} />
            <Route path="/cabling/canvas" element={<CablingCanvas companyId={selectedCompany} />} />
            <Route path="/infrastructure/cabling" element={<Navigate to="/cabling/designer" replace />} />

            <Route path="/infrastructure/workstations" element={<WorkstationsDashboard companyId={selectedCompany} />} />
            <Route path="/infrastructure/workstations/list" element={<WorkstationsList companyId={selectedCompany} />} />
            <Route path="/infrastructure/workstations/updates" element={<WorkstationsUpdatesPage companyId={selectedCompany} />} />
            <Route path="/infrastructure/workstations/software" element={<WorkstationsSoftwarePage companyId={selectedCompany} />} />
            <Route path="/infrastructure/workstations/vulnerabilities" element={<WorkstationsVulnerabilitiesPage companyId={selectedCompany} />} />
            <Route path="/infrastructure/workstations/plans" element={<WorkPlansList companyId={selectedCompany} scopeType="workstation" />} />
            {/* Planes de trabajo: transversal, a nivel general (no en Infraestructura). */}
            <Route path="/work-plans" element={<WorkPlansList companyId={selectedCompany} />} />
            <Route path="/work-plans/:planId" element={<WorkPlanDetail companyId={selectedCompany} />} />
            <Route path="/infrastructure/work-plans" element={<Navigate to="/work-plans" replace />} />
            <Route path="/infrastructure/ip-phones" element={<IPPhonesList companyId={selectedCompany} />} />
            <Route path="/infrastructure/printers" element={<PrintersList companyId={selectedCompany} />} />
            <Route path="/infrastructure/peripherals" element={<PeripheralsList companyId={selectedCompany} />} />
            <Route path="/infrastructure/chassis" element={<ChassisList companyId={selectedCompany} />} />
            <Route path="/infrastructure/power-canvas" element={<PowerCanvas companyId={selectedCompany} />} />
            <Route path="/infrastructure/network-canvas" element={<NetworkCanvas companyId={selectedCompany} />} />
            <Route path="/infrastructure/switch-designer" element={<Navigate to="/cabling/wiring" replace />} />
            <Route path="/infrastructure/network-topology" element={<NetworkTopologyCanvas companyId={selectedCompany} />} />
            <Route path="/infrastructure/network-discovered" element={<NetworkDiscoveredList companyId={selectedCompany} />} />
            <Route path="/infrastructure/connections" element={<ConnectionsMatrix companyId={selectedCompany} />} />
            <Route path="/infrastructure/datacenter-diagram" element={<DatacenterDiagram companyId={selectedCompany} />} />
            
            {/* Redes / IPAM */}
            <Route path="/networks" element={<IPAMDashboard companyId={selectedCompany} />} />
            <Route path="/networks/blocks" element={<IPAMBlocks companyId={selectedCompany} />} />
            <Route path="/networks/subnets" element={<IPAMSubnets companyId={selectedCompany} />} />
            <Route path="/networks/explorer" element={<IPAMExplorer companyId={selectedCompany} />} />
            <Route path="/networks/assignments" element={<IPAMAssignments companyId={selectedCompany} />} />
            <Route path="/networks/vlans" element={<IPAMVlans companyId={selectedCompany} />} />
            <Route path="/networks/ips" element={<IPAMIPs companyId={selectedCompany} />} />
            <Route path="/networks/vips" element={<IPAMVirtualGroups companyId={selectedCompany} />} />
            <Route path="/networks/dhcp" element={<IPAMDhcp companyId={selectedCompany} />} />

            {/* Virtualización */}
            <Route path="/virt/overview"    element={<VirtDashboard         companyId={selectedCompany} />} />
            <Route path="/virt/topology"   element={<VirtTopologyCanvas    companyId={selectedCompany} />} />
            <Route path="/virt/hypervisors" element={<VirtHypervisorsList companyId={selectedCompany} />} />
            <Route path="/virt/clusters" element={<VirtClustersList companyId={selectedCompany} />} />
            <Route path="/virt/datastores" element={<UnderConstruction sectionName="Datastores" sectionIcon="🗄️" />} />
            <Route path="/virt/vswitches" element={<VirtVSwitchesList companyId={selectedCompany} />} />
            <Route path="/virt/vms" element={<VMsDashboard companyId={selectedCompany} />} />
            <Route path="/virt/vms/list" element={<VirtVMsList companyId={selectedCompany} />} />
            {/* Cloud / VPS: MISMO listado, filtrado a lo que no corre en un
                hipervisor propio (nubes públicas y máquinas cedidas). */}
            <Route path="/virt/cloud" element={<VirtVMsList companyId={selectedCompany} scope="cloud" />} />

            {/* WiFi */}
            <Route path="/wifi/topology"  element={<WifiTopologyCanvas companyId={selectedCompany} />} />
            {/* El mapa de planta es el mismo del cableado, con la fuente de datos del WiFi. */}
            <Route path="/wifi/map"       element={<FloorPlanMap companyId={selectedCompany} mode="aps" />} />
            <Route path="/wifi/dashboard" element={<WifiDashboard companyId={selectedCompany} />} />
            <Route path="/wifi/controllers" element={<WifiControllersList companyId={selectedCompany} />} />
            <Route path="/wifi/aps" element={<WifiAccessPointsList companyId={selectedCompany} />} />
            <Route path="/wifi/radius-servers" element={<RadiusServersList companyId={selectedCompany} />} />

            {/* WAN / Operadores ISP */}
            <Route path="/wan/operators" element={<ISPOperators companyId={selectedCompany} />} />
            <Route path="/wan/circuits"  element={<ISPCircuits  companyId={selectedCompany} />} />
            <Route path="/wan/routers"   element={<RoutersList  companyId={selectedCompany} />} />

            <Route path="/systems" element={<UnderConstruction sectionName="Sistemas y Virtualización" sectionIcon="💻" />} />
            <Route path="/applications" element={<UnderConstruction sectionName="Aplicaciones y Software" sectionIcon="📱" />} />
            <Route path="/security" element={<UnderConstruction sectionName="Seguridad" sectionIcon="🔒" />} />
            <Route path="/services" element={<ServicesList companyId={selectedCompany} />} />
            <Route path="/services/designer" element={<ServiceDesignerRoute companyId={selectedCompany} />} />
            <Route path="/services/dependency-map" element={<ServiceDependencyMap companyId={selectedCompany} />} />
            <Route path="/services/farms" element={<ServiceFarmsList companyId={selectedCompany} />} />
            <Route path="/backups" element={<BackupPoliciesList companyId={selectedCompany} />} />
            <Route path="/apps/databases" element={<DatabaseInstancesList companyId={selectedCompany} />} />
            <Route path="/apps/licenses" element={<LicensesList companyId={selectedCompany} />} />
            <Route path="/apps/web-sites" element={<WebSitesList companyId={selectedCompany} />} />
            <Route path="/documentation" element={<DocsLibrary companyId={selectedCompany} />} />
            <Route path="/documentation-empresa" element={<CompanyDocs companyId={selectedCompany} />} />
            <Route path="/documentation/new" element={<DocsEditor companyId={selectedCompany} />} />
            <Route path="/documentation/:id" element={<DocsEditor companyId={selectedCompany} />} />
            <Route path="/manual" element={<ManualCMDB companyId={selectedCompany} />} />
            {/* Academia CMDB: sustituye a los tutoriales guiados (docs/ACADEMIA_CMDB.md). */}
            <Route path="/academia/*" element={<AcademyPage />} />
            <Route path="/tutoriales" element={<Navigate to="/academia" replace />} />
            <Route path="/feedback" element={<FeedbackHub companyId={selectedCompany} />} />

            {/* Documentos a pantalla completa */}
            <Route path="/docs/:objectType/:objectId" element={<DocumentWorkspace companyId={selectedCompany} />} />
            
            {/* Administración */}
            <Route path="/admin" element={<Settings companyId={selectedCompany} companyTheme={companyTheme} />} />
            <Route path="/admin/naming" element={<NamingManager companyId={selectedCompany} />} />
            <Route path="/admin/catalog" element={<DeviceCatalogManager companyId={selectedCompany} />} />
            <Route path="/admin/roles" element={<RolesManager companyId={selectedCompany} />} />
            <Route path="/admin/setup" element={<SetupManager companyId={selectedCompany} />} />
            <Route path="/admin/agent-mapping" element={<AgentMappingProfile companyId={selectedCompany} embedded={false} />} />
            {/* Panel de administración de la plataforma CMDB (solo superadmin) */}
            <Route path="/admin/platform" element={<PlatformAdmin userInfo={userInfo} />} />
            <Route path="/audit" element={<AuditLog companyId={selectedCompany} />} />
            <Route path="/settings" element={<Settings companyId={selectedCompany} companyTheme={companyTheme} />} />
            <Route path="/settings/agent" element={<AgentSettings companyId={selectedCompany} />} />

            {/* Mi perfil: aspecto de la app + enlace a la identidad del IdP */}
            <Route
              path="/perfil"
              element={(
                <MiPerfil
                  user={userInfo}
                  companyId={selectedCompany}
                  companyTheme={companyTheme}
                  onLogout={handleLogout}
                />
              )}
            />
            
            {/* Legacy routes for backwards compatibility */}
            <Route path="/racks/:locationId" element={<RacksList companyId={selectedCompany} />} />
            <Route path="/rack/:rackId" element={<RackViewer companyId={selectedCompany} />} />
            
            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
          </ViewsProvider>
        </MainLayout>

        {/* PWA: banda «Instala el CMDB». Solo aparece si el navegador ofrece
            instalar (Android/Chrome) o si es iPhone/iPad; nunca en escritorio
            ancho ni con la app ya instalada. */}
        <InstallAppPrompt />

        {/* Primera vez del usuario: idioma + tema. Se pinta ENCIMA de la app
            (que ya está cargada) para que al elegir se vea el cambio en vivo. */}
        {onboardingNeeded && (
          <OnboardingWizard
            user={userInfo}
            companyId={selectedCompany}
            companyTheme={companyTheme}
            saving={onboardingSaving}
            onComplete={handleOnboardingComplete}
          />
        )}
        </>
          )}
        />
      </Routes>
    </Router>
    </SocketProvider>
  );
}

/**
 * ServiceDesignerRoute — catalog first, then the graph editor for a chosen
 * service. State is kept local so the URL stays simple (/services/designer).
 * A "nav stack" lets the user open a linked service from the graph and later
 * go back to the previous service(s) using the breadcrumb / back button.
 */
function ServiceDesignerRoute({ companyId }) {
  // Pila de servicios abiertos: [{ id, name }]. La lista raíz es la base.
  const [stack, setStack] = useState([]);
  // Pila del "diagrama de un elemento" (spec §18): [{ ciType, ciId, name }].
  // Se puede saltar de un elemento a otro (cada uno centra el grafo en él).
  const [focusStack, setFocusStack] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Enlace directo a un servicio concreto (fichas que enlazan al diseñador,
  // p. ej. el servidor RADIUS): /services/designer?service=<id>
  const deepLinkId = searchParams.get('service');
  useEffect(() => {
    if (!deepLinkId) return;
    const id = parseInt(deepLinkId, 10);
    // Consumimos el parámetro para que "volver a la lista" no lo reabra.
    setSearchParams({}, { replace: true });
    if (Number.isNaN(id) || id <= 0) return;
    setStack([{ id, name: `Servicio #${id}` }]);
    // Mejor esfuerzo: poner el nombre real en la miga de pan.
    api.getService(id, companyId)
      .then((svc) => {
        if (!svc || !svc.id) return;
        const name = svc.display_name || svc.name || `Servicio #${id}`;
        setStack((s) => (s.length === 1 && s[0].id === id ? [{ id, name }] : s));
      })
      .catch(() => {});
  }, [deepLinkId, companyId, setSearchParams]);

  // Enlace directo al DIAGRAMA DE UN ELEMENTO (spec §18, «de qué depende y dónde
  // impacta»). Las listas del CMDB enlazan aquí para no obligar a buscar el
  // servicio a mano:
  //   /services/designer?ci_type=virt_vm&ci_id=<id>&ci_name=<nombre>
  const deepLinkCiType = searchParams.get('ci_type');
  const deepLinkCiId = searchParams.get('ci_id');
  const deepLinkCiName = searchParams.get('ci_name');
  useEffect(() => {
    if (!deepLinkCiType || !deepLinkCiId) return;
    setSearchParams({}, { replace: true });
    const id = parseInt(deepLinkCiId, 10);
    if (Number.isNaN(id) || id <= 0) return;
    setFocusStack([{
      ciType: deepLinkCiType,
      ciId: id,
      name: deepLinkCiName || `${deepLinkCiType} #${id}`,
    }]);
  }, [deepLinkCiType, deepLinkCiId, deepLinkCiName, setSearchParams]);

  const current = stack.length ? stack[stack.length - 1] : null;
  const focus = focusStack.length ? focusStack[focusStack.length - 1] : null;

  // Al cambiar de servicio se sale del diagrama del elemento (el foco pertenece
  // al servicio que estabas mirando). Ojo: NO en el primer montaje, porque el
  // enlace directo a un elemento (`?ci_type=&ci_id=`) acaba de poner el foco y
  // este efecto lo borraría.
  const prevServiceId = useRef(current?.id);
  useEffect(() => {
    if (prevServiceId.current === current?.id) return;
    prevServiceId.current = current?.id;
    setFocusStack([]);
  }, [current?.id]);

  // Con foco hay grafo, aunque se haya llegado sin servicio (enlace directo al
  // diagrama de un elemento desde las listas del CMDB).
  if (current || focus) {
    return (
      // Sin `key`: el componente NO se desmonta al saltar a otro servicio, así
      // la pantalla completa (fullscreen) se mantiene entre servicios. El
      // propio grafo recarga sus datos cuando cambia serviceId.
      <ServiceDesignerGraph
        serviceId={current ? current.id : null}
        companyId={companyId}
        navStack={stack}
        // Volver = desapilar (a la lista si solo queda la raíz).
        onBack={() => setStack((s) => s.slice(0, -1))}
        onNavigateToIndex={(i) => setStack((s) => s.slice(0, i + 1))}
        onGoToList={() => setStack([])}
        // Abrir un servicio enlazado desde el grafo: lo apilamos.
        onOpenService={(id, svc) =>
          setStack((s) => [...s, { id, name: svc?.display_name || svc?.name || `Servicio #${id}` }])
        }
        // Diagrama centrado en un elemento: de qué depende y dónde impacta.
        focusCi={focus}
        focusTrail={focusStack}
        onFocusCi={(node) =>
          setFocusStack((s) => [
            ...s,
            {
              ciType: node.ci_type,
              ciId: node.ci_id,
              name: node.name || node.logical_label || `${node.ci_type} ${node.ci_id}`,
            },
          ])
        }
        onExitFocus={() => setFocusStack((s) => s.slice(0, -1))}
        onExitFocusAll={() => setFocusStack([])}
        onFocusToIndex={(i) => setFocusStack((s) => s.slice(0, i + 1))}
      />
    );
  }
  return (
    <ServiceDesignerList
      companyId={companyId}
      onOpenService={(id, svc) => setStack([{ id, name: svc?.display_name || svc?.name || `Servicio #${id}` }])}
    />
  );
}

export default App;
